import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { assign as radashAssign } from 'radash';

import { BaseChartComponent } from '@connect-services/client/components/statistics/base-chart/base-chart.component';
import { ChartLayoutComponent } from '@connect-services/client/components/statistics/chart-layout/chart-layout.component';
import {
    BaseChartOptions,
    ChartComponentInterface,
    ChartService,
    collapsedHeightChart,
    commonOptions,
    expandedHeightChart,
    Series,
    SeriesFormated,
} from '@connect-services/client/services/chart.service';
import { ComputedStatistic, LineColumnComputedStatisticResult } from '@connect-services/src/sdk/api';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BaseChartComponent, ChartLayoutComponent],
    selector: 'app-line-chart',
    styleUrl: './line-chart.component.scss',
    templateUrl: './line-chart.component.html',
})
export class LineChartComponent implements ChartComponentInterface {
    public readonly computedStatistic = input.required<ComputedStatistic>();
    public readonly hoverSubtitleInformation = signal<string>('');
    public readonly hoverTitleInformation = signal<string>('');
    public readonly hoverTitleInformationColor = computed<string>(
        () => this.computedStatistic()?.renderOptions?.colors?.[0] ?? '#2f2f2f',
    );
    public readonly isExpanded = input.required<boolean>();
    public readonly isPrintScreen = input<boolean>(false);
    public readonly options = computed<BaseChartOptions>(() => {
        const computedStatistic = this.computedStatistic();
        const renderOptions = computedStatistic.renderOptions;

        if (!computedStatistic.renderOptions) {
            return {} as BaseChartOptions;
        }

        const series = this.chartService.setSeries(computedStatistic);
        const categories = this.chartService.getCategories(
            this.computedStatistic().result!.data as LineColumnComputedStatisticResult[],
        );
        const alignSeries: SeriesFormated[] = this.chartService.alignMultiplesSeries(series as Series, categories);

        return radashAssign(commonOptions, {
            chart: {
                animations: {
                    enabled: !this.isPrintScreen(),
                },
                events: {
                    mounted: () => {
                        this.ready.emit();
                    },
                    mouseLeave: (): void => {
                        this.hoverTitleInformation.set('');
                    },
                    mouseMove: (_event, _chartContext, opts) => {
                        const { dataPointIndex, seriesIndex } = opts;
                        if (dataPointIndex >= 0 && seriesIndex >= 0) {
                            const titleInformation = `${opts?.config?.series?.[seriesIndex]?.data?.[dataPointIndex]} ${renderOptions.unit?.y ?? ''}`;
                            this.hoverTitleInformation.set(titleInformation);
                        }
                    },
                },
                height: this.isExpanded() ? expandedHeightChart : collapsedHeightChart,
                toolbar: {
                    show: false,
                },
                type: 'area',
                zoom: {
                    enabled: false,
                },
            },
            dataLabels: {
                enabled: this.isPrintScreen(),
            },
            fill: {
                colors: renderOptions.colors ?? [],
                gradient: {
                    opacityFrom: 0.7,
                    opacityTo: 0.9,
                    shadeIntensity: 1,
                    stops: [0, 90, 100],
                },
                type: 'gradient',
            },
            legend: {
                horizontalAlign: 'left',
                labels: {
                    colors: renderOptions.colors ?? [],
                },
                markers: {
                    fillColors: renderOptions.colors ?? [],
                },
                offsetY: 10,
                position: renderOptions.legend?.position,
            },
            plotOptions: {
                bar: {
                    columnWidth: '50%',
                },
            },
            series: alignSeries
                ? alignSeries.map((alignSerie) => {
                      return {
                          data: alignSerie.data as (number | null)[],
                          name: alignSerie.name,
                      };
                  })
                : undefined,
            tooltip: {
                items: {
                    display: 'none',
                },
                x: {
                    show: false,
                },
            },
            xaxis: {
                categories: categories,
                crosshairs: {
                    fill: {
                        gradient: {
                            colorFrom: renderOptions?.colors?.[0] ?? '#838383', //Remove? (fill)
                            colorTo: renderOptions?.colors?.[1] ?? '#d7d7d7', //Remove? (fill)
                        },
                    },
                },
                title: {
                    text: this.chartService.formatAxisText(
                        renderOptions.xaxisTitle?.text ?? '',
                        renderOptions.unit?.x ?? '',
                    ),
                },
            },

            yaxis: {
                show: this.isPrintScreen() ? this.isPrintScreen() : renderOptions.yaxisTitle?.show,
                title: {
                    text: this.chartService.formatAxisText(renderOptions.yaxisTitle?.text, renderOptions.unit?.y),
                },
            },
        });
    });

    public ready = output<void>();

    protected chartService = inject(ChartService);
}
